import { Interval } from 'src/services/apollo/generated/graphql'
import type { ValueOf } from 'type-fest'

/*
 * ==== Membership Plan ========================================================
 */

const MembershipTierId = {
  1: 'membership-start-v2',
  2: 'membership-plus-v2',
  3: 'membership-premium-v2',
} as const
type MembershipTierId = ValueOf<typeof MembershipTierId>

const AndroidMembershipProductId = {
  1: {
    [Interval.Month]: 'iap-membership-start-month',
    [Interval.Year]: 'iap-membership-start-year',
  },
  2: {
    [Interval.Month]: 'iap-membership-plus-month',
    [Interval.Year]: 'iap-membership-plus-year-2',
  },
  3: {
    [Interval.Month]: 'iap-membership-premium-month',
    [Interval.Year]: 'iap-membership-premium-year-2',
  },
} as const
type AndroidMembershipProductId = ValueOf<
  ValueOf<typeof AndroidMembershipProductId>
>

const IosMembershipProductId = {
  1: {
    [Interval.Month]: 'subscription.premium.monthly',
    [Interval.Year]: 'subscription.premium.yearly',
  },
  2: {
    [Interval.Month]: 'subscription.plus.monthly',
    [Interval.Year]: 'subscription.plus.yearly',
  },
  3: {
    [Interval.Month]: 'subscription.unlimited.monthly',
    [Interval.Year]: 'subscription.unlimited.yearly',
  },
} as const
type IosMembershipProductId = ValueOf<ValueOf<typeof IosMembershipProductId>>

/**
 * Map membership tier number to membership tier ID
 *
 * @example mapTierNumberToId(2) // "membership-plus-v2"
 */
export function mapTierNumberToId(tier: number): MembershipTierId | undefined {
  return tier in MembershipTierId
    ? MembershipTierId[tier as keyof typeof MembershipTierId]
    : undefined
}

/**
 * Map membership tier number to mobile product ID
 *
 * @example mapTierNumberToMobileProductId(2, Interval.Year, 'iOS') // "subscription.plus.yearly"
 */
export function mapTierNumberToMobileProductId(
  tier: number,
  billingInterval: Interval,
  platform: 'Android' | 'iOS',
): AndroidMembershipProductId | IosMembershipProductId | undefined {
  const ids =
    platform === 'iOS' ? IosMembershipProductId : AndroidMembershipProductId
  return tier in ids
    ? ids[tier as keyof typeof ids][billingInterval]
    : undefined
}

/*
 * ==== Credit Pack ============================================================
 */

const AndroidCreditPackProductId = {
  'extra-package': 'iap_credits_pack',
  'extra-package-2': 'iap_credits_pack_2',
  'extra-package-3': 'iap_credits_pack_3',
  'extra-package-5': 'iap_credits_pack_5',
  'extra-package-9': 'iap_credits_pack_180',
} as const
type AndroidCreditPackProductId = ValueOf<typeof AndroidCreditPackProductId>

const IosCreditPackProductId = {
  'extra-package': 'consumable.extra.package',
  'extra-package-2': 'consumable.extra.package.2',
  'extra-package-3': 'consumable.extra.package.3',
  'extra-package-5': 'consumable.extra.package.5',
  'extra-package-9': 'consumable.extra.package.9',
} as const
type IosCreditPackProductId = ValueOf<typeof IosCreditPackProductId>

/**
 * Map credit pack ID to mobile product ID
 *
 * @example mapCreditPackIdToMobileProductId('extra-package-2', 'Android') // "iap_credits_pack_2"
 */
export function mapCreditPackIdToMobileProductId(
  creditPackId: string,
  platform: 'Android' | 'iOS',
): AndroidCreditPackProductId | IosCreditPackProductId | undefined {
  const ids =
    platform === 'iOS' ? IosCreditPackProductId : AndroidCreditPackProductId
  return creditPackId in ids ? ids[creditPackId as keyof typeof ids] : undefined
}
